import React, { ReactElement } from 'react';
import { QHeading, QText } from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { ReassignItemsMap, ReassignDocumentsMap, ReassignDocumentTemplatesMap } from '../../types';
import { filterUniqueOwners } from '../../utils';
import { BasicSummaryCategories } from './types';

type StandardSummaryItemProps = {
  items: ReassignDocumentsMap | ReassignDocumentTemplatesMap | ReassignItemsMap;
  summaryCategory: BasicSummaryCategories;
  note?: string;
};

/**
 * This component renders the summary items for each of the basic categories,
 * i.e. "3 issue actions reassigned to Some User"
 */
const StandardSummaryItem = ({ items, summaryCategory, note }: StandardSummaryItemProps): ReactElement => {
  const { t } = useTranslation();
  const { eventCollaborators } = useFlags();
  const uniqueOwners = filterUniqueOwners(items);

  const getTitleKey = (category: BasicSummaryCategories) => {
    const titleMap = {
      approver: 'reassignEntities.summary.approvalActions',
      reviewer: 'reassignEntities.summary.reviewActions',
      issues: eventCollaborators ? 'reassignEntities.summary.eventActions' : 'reassignEntities.summary.issueActions',
      tasks: 'reassignEntities.summary.taskActions',
      changeRequestOwner: 'reassignEntities.summary.changeRequests',
      control: 'reassignEntities.summary.controls',
      documentTemplateOwner: 'reassignEntities.summary.documentTemplates',
    };
    return titleMap[category];
  };

  const getActionType = (category: BasicSummaryCategories) => {
    const actionTypeMap = {
      approver: 'approval',
      reviewer: 'review',
      issues: eventCollaborators ? 'event' : 'issue',
      tasks: 'task',
      changeRequestOwner: 'changeRequestOwner',
      control: 'control',
      documentTemplateOwner: 'documentTemplateOwner',
    };
    return actionTypeMap[category];
  };

  return (
    <QHeading size="sm" fontSize="md" fontWeight="bold">
      {t(getTitleKey(summaryCategory))}
      {uniqueOwners.map(([owner, count]) => {
        const translationKey =
          count === 1 ? 'reassignEntities.summary.actionTransfer' : 'reassignEntities.summary.actionTransferPlural';
        const translatedText = t(translationKey, { count, type: getActionType(summaryCategory), name: owner });

        return (
          <QText key={owner} fontSize="xs" fontWeight="light">
            {translatedText}
          </QText>
        );
      })}
      {note && (
        <QText fontSize="xs" fontWeight="light">
          {note}
        </QText>
      )}
    </QHeading>
  );
};

export default StandardSummaryItem;
